import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState } from 'react';

import { useAuth, useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import { materializedComingUpDocPath } from '../schemas/paths';
import type {
  ComingUpEvent,
  MaterializedComingUp,
} from '../schemas/materializedComingUp';

export interface UseMaterializedComingUpResult {
  /** Sorted-ascending list of upcoming events for the current user.
   *  Empty array until the materialized doc hydrates (or if the user
   *  has no qualifying recipients). The FE typically filters further
   *  to the next ~60 days for display. */
  upcoming: ComingUpEvent[];
  hydrated: boolean;
  error: Error | null;
  /** The account this list belongs to (`null` before auth resolves). Lets a
   *  caller key per-user UI memory — e.g. the strip's remembered scroll
   *  offset — so one account never inherits another's (THE-1403). */
  uid: string | null;
}

/**
 * Subscribes to `theaWebUser/{uid}/materializedComingUp/current` and
 * returns the trigger-materialized upcoming-events list.
 *
 * The materialized doc is BE-written from a Firestore trigger
 * (`materialized_coming_up_trigger.py`) — derived data, sorted, with
 * recipient name/emoji denormalized. The FE just reads. Pattern
 * matches `useRecipients` for the auth-uid handling.
 *
 * Behavior notes:
 * - When the doc doesn't exist yet (anon user before any recipient
 *   with dates exists), we surface `upcoming: []` + `hydrated: true`
 *   so callers can render an empty state rather than wait forever.
 * - On auth uid swap (anon → permanent via mergeGiftFlow), the
 *   listener resets and re-binds — no stale anon entries.
 */
export function useMaterializedComingUp(): UseMaterializedComingUpResult {
  const auth = useAuth();
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [upcoming, setUpcoming] = useState<ComingUpEvent[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      const nextUid = user?.uid ?? null;
      setUid((prev) => {
        if (prev === nextUid) return prev;
        setUpcoming([]);
        setHydrated(false);
        setError(null);
        return nextUid;
      });
    });
  }, [auth]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    setError(null);

    // Mirror useRecipients' defensive fallback: test contexts may not
    // wire `ensureAuth`. Treat as "no uid" — empty list, mark hydrated.
    if (typeof ensureAuth !== 'function') {
      setHydrated(true);
      return;
    }

    // Bind the identity this effect run was SCHEDULED for, not whatever
    // `ensureAuth()` happens to resolve. During an anon→permanent transition
    // `ensureAuth()` can hand back the OLD uid a beat late (a stale in-flight
    // anon sign-in, or a separate hook instance); `uid` from the authoritative
    // `onAuthStateChanged` is this run's real identity (a newer one would have
    // cancelled it). `resolvedUid` is used ONLY to bootstrap the null case
    // (cold load / fresh anon mint), so a settled account is never stranded
    // without a subscription.
    const expectedUid = uid;

    ensureAuth()
      .then((resolvedUid) => {
        if (cancelled) return;
        // Known identity → bind it directly; `resolvedUid` bootstraps null.
        const targetUid = expectedUid ?? resolvedUid;
        const ref = doc(db, ...materializedComingUpDocPath(targetUid));
        unsubscribe = onSnapshot(
          ref,
          (snap) => {
            if (cancelled) return;
            if (!snap.exists()) {
              // No materialized doc yet — anon user before any recipient
              // with dates, or a brand-new account. Surface empty +
              // hydrated so the Coming Up strip can render its empty
              // state instead of a perpetual loading state.
              setUpcoming([]);
              setHydrated(true);
              return;
            }
            const data = snap.data() as MaterializedComingUp | undefined;
            setUpcoming(data?.upcoming ?? []);
            setHydrated(true);
          },
          (err) => {
            if (cancelled) return;
            setError(err);
            setHydrated(true);
          },
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setHydrated(true);
      });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [uid, db, ensureAuth]);

  return { upcoming, hydrated, error, uid };
}
